import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { api } from '@/api/client';
import { sourcingApi, type SearchProject } from '@/api/sourcing';
import type { Vacancy } from '@/types';

export default function Sourcing() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [projects, setProjects] = useState<SearchProject[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showNew, setShowNew] = useState(false);

  const load = () => {
    sourcingApi
      .listProjects()
      .then(setProjects)
      .catch((e) => setError(e.message));
  };

  useEffect(load, []);

  return (
    <div className="max-w-5xl">
      <div className="flex items-start justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-heading font-bold text-foreground-950">
            {t('sourcing.title')}
          </h1>
          <p className="text-sm text-foreground-500 mt-1">{t('sourcing.subtitle')}</p>
        </div>
        <button
          onClick={() => setShowNew(true)}
          className="shrink-0 px-4 py-2.5 rounded-xl bg-primary-500 text-white text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer flex items-center gap-1.5"
        >
          <i className="ri-add-line" />
          {t('sourcing.newProject')}
        </button>
      </div>

      {error && <p className="text-sm text-red-600 mb-4">{error}</p>}
      {!projects && !error && <p className="text-sm text-foreground-400">{t('sourcing.loading')}</p>}
      {projects && projects.length === 0 && (
        <p className="text-sm text-foreground-400">{t('sourcing.empty')}</p>
      )}

      <div className="space-y-3">
        {projects?.map((p) => (
          <button
            key={p.id}
            onClick={() => navigate(`/sourcing/${p.id}`)}
            className="w-full text-left bg-white border border-background-200 rounded-2xl px-5 py-4 hover:border-primary-300 hover:shadow-sm transition-all cursor-pointer"
          >
            <div className="flex items-center justify-between gap-4">
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <span className="font-heading font-semibold text-foreground-900 truncate">
                    {p.name}
                  </span>
                  <span
                    className={`text-[10px] uppercase font-semibold px-1.5 py-0.5 rounded-full ${
                      p.status === 'active'
                        ? 'bg-green-100 text-green-700'
                        : 'bg-background-200 text-foreground-500'
                    }`}
                  >
                    {p.status}
                  </span>
                  <span className="text-[10px] uppercase font-semibold px-1.5 py-0.5 rounded-full bg-background-100 text-foreground-500">
                    {p.language}
                  </span>
                </div>
                {p.candidate_profile?.text && (
                  <p className="text-xs text-foreground-500 mt-1 line-clamp-1">
                    {p.candidate_profile.text}
                  </p>
                )}
              </div>
              <div className="shrink-0 text-right">
                <p className="text-sm font-mono font-semibold text-foreground-800">
                  {p.candidates_count ?? 0}{' '}
                  <span className="text-foreground-400 font-normal">{t('sourcing.candidates')}</span>
                </p>
                <p className="text-xs text-foreground-400 mt-0.5">
                  {p.scored_count ?? 0} {t('sourcing.scored')}
                </p>
              </div>
            </div>
          </button>
        ))}
      </div>

      {showNew && (
        <NewProjectModal
          onClose={() => setShowNew(false)}
          onCreated={(id) => {
            setShowNew(false);
            navigate(`/sourcing/${id}`);
          }}
        />
      )}
    </div>
  );
}

function NewProjectModal({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: (id: string) => void;
}) {
  const { t } = useTranslation();
  const [vacancies, setVacancies] = useState<Vacancy[]>([]);
  const [name, setName] = useState('');
  const [language, setLanguage] = useState('en');
  const [vacancyId, setVacancyId] = useState('');
  const [portrait, setPortrait] = useState('');
  const [outreachTemplate, setOutreachTemplate] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api.get<Vacancy[]>('/vacancies').then(setVacancies).catch(() => setVacancies([]));
  }, []);

  const submit = async () => {
    if (!name.trim()) return;
    setSaving(true);
    setError(null);
    try {
      const project = await sourcingApi.createProject({
        name: name.trim(),
        language,
        vacancy_id: vacancyId || null,
        candidate_profile: {
          text: portrait.trim() || undefined,
          outreach_template: outreachTemplate.trim() || undefined,
        },
      });
      onCreated(project.id);
    } catch (e) {
      setError((e as Error).message);
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl w-full max-w-lg p-6 max-h-[90vh] overflow-y-auto">
        <h2 className="text-lg font-heading font-semibold text-foreground-900 mb-4">
          {t('sourcing.newProject')}
        </h2>
        <div className="space-y-4">
          <div>
            <label className="text-xs font-medium text-foreground-500 mb-1 block">
              {t('sourcing.projectName')}
            </label>
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Treejar CEO-track, bucket A"
              className="w-full text-sm border border-background-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-1 focus:ring-primary-400"
            />
          </div>
          <div className="flex gap-3">
            <div className="w-32">
              <label className="text-xs font-medium text-foreground-500 mb-1 block">
                {t('sourcing.language')}
              </label>
              <select
                value={language}
                onChange={(e) => setLanguage(e.target.value)}
                className="w-full text-sm border border-background-200 rounded-lg px-2 py-2 cursor-pointer"
              >
                <option value="en">en</option>
                <option value="ru">ru</option>
              </select>
            </div>
            <div className="flex-1">
              <label className="text-xs font-medium text-foreground-500 mb-1 block">
                {t('sourcing.vacancy')}
              </label>
              <select
                value={vacancyId}
                onChange={(e) => setVacancyId(e.target.value)}
                className="w-full text-sm border border-background-200 rounded-lg px-2 py-2 cursor-pointer"
              >
                <option value="">{t('sourcing.noVacancy')}</option>
                {vacancies.map((v) => (
                  <option key={v.id} value={v.id}>
                    {v.title}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div>
            <label className="text-xs font-medium text-foreground-500 mb-1 block">
              {t('sourcing.portrait')}
            </label>
            <textarea
              value={portrait}
              onChange={(e) => setPortrait(e.target.value)}
              rows={5}
              className="w-full text-sm border border-background-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-1 focus:ring-primary-400"
            />
          </div>
          <div>
            <label className="text-xs font-medium text-foreground-500 mb-1 block">
              {t('sourcing.outreachTemplate')}
            </label>
            <textarea
              value={outreachTemplate}
              onChange={(e) => setOutreachTemplate(e.target.value)}
              rows={3}
              className="w-full text-sm border border-background-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-1 focus:ring-primary-400"
            />
          </div>
          {error && <p className="text-sm text-red-600">{error}</p>}
          <div className="flex justify-end gap-2 pt-2">
            <button
              onClick={onClose}
              className="px-4 py-2 rounded-xl text-sm text-foreground-600 hover:bg-background-100 cursor-pointer"
            >
              {t('sourcing.cancel')}
            </button>
            <button
              onClick={submit}
              disabled={saving || !name.trim()}
              className="px-4 py-2 rounded-xl bg-primary-500 text-white text-sm font-medium hover:bg-primary-600 disabled:opacity-50 cursor-pointer"
            >
              {t('sourcing.create')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
